"use client";

import { useEffect, useState } from "react";
import { get, put } from "../../redux/ApiHelper";
import { LinesInterface } from "../../interfaces/info.interface";
import { successToast } from "../../utils/toast.util";

const EMPTY_SLIDES: LinesInterface[] = [
  { line1: "", line2: "" },
  { line1: "", line2: "" },
  { line1: "", line2: "" },
];

const ensureThree = (arr?: LinesInterface[] | null): LinesInterface[] => {
  if (!Array.isArray(arr) || arr.length === 0) {
    return EMPTY_SLIDES.map((s) => ({ ...s }));
  }
  const next = arr.slice(0, 3).map((s) => ({
    line1: s?.line1 ?? "",
    line2: s?.line2 ?? "",
  }));
  while (next.length < 3) {
    next.push({ line1: "", line2: "" });
  }
  return next;
};

type RoleSectionProps = {
  title: string;
  items: LinesInterface[];
  onChange: (index: number, field: keyof LinesInterface, value: string) => void;
};

const RoleSliderSection = ({ title, items, onChange }: RoleSectionProps) => (
  <div className="flex flex-col gap-4 p-2 border border-gray-300 rounded-md bg-white shadow-sm">
    <p className="font-semibold text-gray-800">{title}</p>
    {items.map((item, index) => (
      <div key={index} className="space-y-2 bg-gray-300 p-2">
        <p>Slider{index + 1}</p>
        <input
          type="text"
          className="w-full p-2 border rounded-md"
          placeholder={`Line 1 - Item ${index + 1}`}
          value={item.line1}
          onChange={(e) => onChange(index, "line1", e.target.value)}
        />
        <input
          type="text"
          className="w-full p-2 border rounded-md"
          placeholder={`Line 2 - Item ${index + 1}`}
          value={item.line2}
          onChange={(e) => onChange(index, "line2", e.target.value)}
        />
      </div>
    ))}
  </div>
);

const MobileAppsliderText = () => {
  const [parentItems, setParentItems] =
    useState<LinesInterface[]>(EMPTY_SLIDES.map((s) => ({ ...s })));
  const [teacherItems, setTeacherItems] =
    useState<LinesInterface[]>(EMPTY_SLIDES.map((s) => ({ ...s })));
  const [initialParent, setInitialParent] = useState<LinesInterface[]>([]);
  const [initialTeacher, setInitialTeacher] = useState<LinesInterface[]>([]);
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    const getItems = async () => {
      try {
        const res = await get("/info/");
        const parent = ensureThree(
          res.parentAuthPageSliderTextArray ?? res.authPageSliderTextArray
        );
        const teacher = ensureThree(
          res.teacherAuthPageSliderTextArray ?? res.authPageSliderTextArray
        );
        setParentItems(parent);
        setTeacherItems(teacher);
        setInitialParent(parent);
        setInitialTeacher(teacher);
      } catch (error) {
        console.log(error);
      }
    };
    getItems();
  }, []);

  const updateItems = async () => {
    try {
      await put(`/info/slider-text-array`, {
        parentAuthPageSliderTextArray: parentItems,
        teacherAuthPageSliderTextArray: teacherItems,
      });
      setInitialParent(parentItems);
      setInitialTeacher(teacherItems);
      setIsChanged(false);
      successToast("Updated successfully");
    } catch (error) {
      console.log(error);
    }
  };

  const handleParentChange = (
    index: number,
    field: keyof LinesInterface,
    value: string
  ) => {
    const updated = [...parentItems];
    updated[index] = { ...updated[index], [field]: value };
    setParentItems(updated);
    setIsChanged(true);
  };

  const handleTeacherChange = (
    index: number,
    field: keyof LinesInterface,
    value: string
  ) => {
    const updated = [...teacherItems];
    updated[index] = { ...updated[index], [field]: value };
    setTeacherItems(updated);
    setIsChanged(true);
  };

  return (
    <div className="w-full max-w-lg mx-auto space-y-6 mt-5">
      <RoleSliderSection
        title="Parent slider text"
        items={parentItems}
        onChange={handleParentChange}
      />
      <RoleSliderSection
        title="Teacher slider text"
        items={teacherItems}
        onChange={handleTeacherChange}
      />
      <button
        onClick={updateItems}
        disabled={!isChanged}
        className={`w-full px-4 py-2 rounded-md text-white font-semibold ${
          isChanged ? "bg-yellow-500 hover:bg-yellow-600" : "bg-gray-400"
        }`}
      >
        Update
      </button>
      <div className="text-sm text-gray-600">
        Parent slides: {parentItems.length} · Teacher slides:{" "}
        {teacherItems.length}
        {isChanged &&
          (JSON.stringify(parentItems) !== JSON.stringify(initialParent) ||
            JSON.stringify(teacherItems) !== JSON.stringify(initialTeacher)) &&
          " · Unsaved changes"}
      </div>
    </div>
  );
};

export default MobileAppsliderText;
